<template>
  <div
    id="scrub-wrapper"
    :class="{
      portrait: sidebarStyle === 'portrait'
    }"
    >
    <div
      id="video-scrub-bar"
      v-show="(
        allowRewind
        && bulbInitialized
      )"
      class="thin"
      :disabled="(
        !allowRewind
        || isLiveSession
      )"
    >
      <button
        v-for="(group, i) in filteredInteractionGroups"
        :key="group.start"
        class="interaction-dot pointer"
        :style="getInteractionDotStyle(group)"
        :aria-label="'jump to interaction group ' + (i + 1)"
        :disabled="(!allowRewind || isLiveSession)"
        @click="seek(group.start)"
      ></button>
    </div>
  </div>
</template>

<script>

// Components:
import EventBus from '@/buses/EventBus';

// Services:
import XApiService from '@/services/XApiService';

// Plugins / Libraries
import _ from 'lodash';
import noUiSlider from 'nouislider';
import moment from 'moment';

import {
	mapGetters,
	mapMutations,
	mapState,
} from 'vuex';

export default {
  name: 'scrub-bar',
  data() {
    return {
      sliderHandle: null,
    }
  },
  methods: {
    ...mapMutations('player', [
      'setDisplayTime',
      'setScrubbing',
      'setSlider',
    ]),
    getInteractionDotStyle(group) {
			return {
				left: `calc(${(group.start / this.displayDuration) * 100}% - 3px)`,
			}
		},
    seek(displayTime) {
			EventBus.$emit(
        'requestAction',
        {
          key: 'seek',
          seekTime: displayTime,
        }
      );
		},
    enableNoUiSliderListeners(slider) {
      if (
        !_.isObject(this.noUiSliderListeners)
      ) return;
      const sliderFinal = slider || this.slider;
      for (const key of Object.keys(this.noUiSliderListeners)) {
        sliderFinal?.on(key, this.noUiSliderListeners[key]);
      }
    },
    disableNoUiSliderListeners(slider) {
      if (
        !_.isObject(this.noUiSliderListeners)
      ) return;
      const sliderFinal = slider || this.slider;
      for (const key of Object.keys(this.noUiSliderListeners)) {
        sliderFinal?.off(key, this.noUiSliderListeners[key]);
      }
    },
    setupScrubBar() {
    	// Create the slider/scrub bar at bottom, range is the total duration of all videos in the bulb:
			const slider = noUiSlider.create(document.getElementById('video-scrub-bar'), {
				animate: false,
			  start: [
          0, // 0
          0, // `start_display_time`
          Math.min(this.maxTime, this.requiredMaxTime) // `required_max_time`
        ],
        // Display a colored bar between the handles:
			  connect: [
          false,
          true,
          true,
          false,
        ],
			  behaviour: 'snap',
			  keyboardSupport: false,
			  step: 1,
			  range: {
					min: 0,
					max: this.displayDuration || 1,
			  },
			  tooltips: [
			  	false,
				  {
				  	to(value) {
				  		if (value >= 3600) {
				  			return moment.utc(value*1000).format('HH:mm:ss');
				  		} else {
				  			return moment.utc(value*1000).format('mm:ss');
				  		}
				  	},
				  	from(value) {
				  		moment.duration(value).asSeconds()
				  	}
				  },
				  false,
			  ],
			});
      this.sliderHandle = document.querySelector('.noUi-handle[data-handle="1"]');
			if (!this.isLiveSession) this.sliderHandle.tabIndex = 0;
			this.sliderHandle.addEventListener('focus', () => {
				this.sliderHandle.setAttribute('aria-valuenow',  this.runtime);
				this.sliderHandle.setAttribute('aria-label',`Scrub bar current time ${this.runtime}`);
			});
      this.setSlider(slider);
      this.enableNoUiSliderListeners(slider);
    },
  },
  computed: {
    ...mapState('player', [
  		'isLiveSession',
      'slider',
    ]),
    ...mapGetters('player', [
      'allowRewind',
      'bulb',
      'bulbInitialized',
      'displayDuration',
      'displayTime',
      'maxTime',
      'requiredMaxTime',
      'sidebarStyle',
    ]),
    ...mapGetters('interactions', [
			'interactionGroups',
		]),
    /**
     * Specific `noUiSlider` events.
     */
     noUiSliderListeners() {
      return {
        'start': () => {
          this.setScrubbing(true);
          // Pause the video to prevent it playing before the drag is complete:
          EventBus.$emit('requestAction', 'pause');
        },
        'update': () => {
          this.sliderHandle.setAttribute('aria-valuenow', this.runtime);
          this.sliderHandle.removeAttribute('aria-valuetext', this.runtime);
          this.sliderHandle.setAttribute('aria-valuemin', 0);
          this.sliderHandle.setAttribute('aria-valuemax', 0)
        },
        'slide': (vals, handleIndex) => {
          if ( // Triggered for all three handles, we only want to seek once.
            handleIndex === 1
            && this.bulbInitialized
          ) {
            // Check if the user can fast forward and the new time is less than the max_time and `required_max_time`:
            const newTime = parseFloat(this.slider.get()[1]);
            if (
              newTime <= this.maxTime
              && (newTime || newTime === 0)
              && this.allowRewind
            ) {
              // If the slider is changed, update the over all display_time to that point on the player:
              this.setDisplayTime(newTime);
              this.$nextTick(() => {
                if (!this.isLiveSession) {
                  EventBus.$emit(
                    'requestAction',
                    {
                      key: 'seek',
                      seekTime: this.displayTime,
                  });
                }
              });
            }
          }
        },
        'end': () => {
          this.setScrubbing(false);
          if (this.slider.get()[1] > this.maxTime) {
            this.slider.set([
              0,
              this.maxTime,
              Math.min(this.maxTime, this.requiredMaxTime)
            ]);
          }
          XApiService.buildSeekVideoStatement({
            activity: {
              id: `https://xapi.playposit.com/activities/video/${this.bulb.id}`,
              name: this.bulb.title,
              seek_time: this.slider.get()[1],
            }
          });
        },
      };
    },
    filteredInteractionGroups() {
    	return _.filter(this.interactionGroups, (g) => g.start <= this.requiredMaxTime && g.start <= this.maxTime);
    },
  },
  mounted() {
    this.setupScrubBar();
  },
  beforeDestroy () {
    this.disableNoUiSliderListeners();
  },
};
</script>

<style lang="scss" scoped>

  #scrub-wrapper {
		flex-grow: 1;
		position: relative;
  }

  #scrub-wrapper.portrait {
  	padding-left: 10px;
  }

  #video-scrub-bar {
		cursor: pointer;
	}

	#video-scrub-bar.thin .noUi-target {
		width: 15px;
    height: 15px;
    left: 0;
    top: 0;
    border: none !important;
	}

	#video-scrub-bar.thin .noUi-base {
		height: 3px;
		position: initial;
	}


</style>
